"use client";

import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { storeCatalogKey, storeDisplayName, type HanaroStore } from "@/lib/hanaro-api";

type StoreComboboxProps = {
  stores: HanaroStore[];
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
};

export function StoreCombobox({
  stores,
  value,
  onValueChange,
  disabled,
  placeholder = "Choose store...",
}: StoreComboboxProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => stores.find((s) => storeCatalogKey(s) === value),
    [stores, value],
  );

  const sortedStores = useMemo(
    () =>
      [...stores]
        .filter((s) => storeCatalogKey(s))
        .sort((a, b) =>
          storeDisplayName(a).localeCompare(storeDisplayName(b), undefined, {
            sensitivity: "base",
          }),
        ),
    [stores],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">{selected ? storeDisplayName(selected) : placeholder}</span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search store..." />
          <CommandList>
            <CommandEmpty>No store found.</CommandEmpty>
            <CommandGroup>
              {sortedStores.map((s) => {
                const key = storeCatalogKey(s);
                const label = storeDisplayName(s);
                return (
                  <CommandItem
                    key={key}
                    value={`${label} ${key}`}
                    onSelect={() => {
                      onValueChange(key);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn("mr-2 size-4", value === key ? "opacity-100" : "opacity-0")}
                    />
                    {label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
